// Sign-in callback failures — a CODE for the screen, the raw text for the log.
//
// `/github-auth-callback` and `/auth/magic-callback` used to interpolate the
// raw failure straight into the one line they paint:
//
//   statusText: `Sign-in failed: ${err?.message || 'unknown error'}`
//   statusText: `OAuth error: ${errorParam}`
//
// That text is English, it comes from the Symbols server or from GitHub, and
// it is in no bundle — so a Georgian arrival read English at the one frame it
// most needs to understand, and no amount of key work could have fixed it.
// Classify the failure to a CODE here instead, and let the code carry the key.
//
// The raw message is NOT discarded: both pages `console.error` it before they
// call this, which is where a support reader needs it.
//
// FA204 / frank trap, the same one `functions/inviteToken.js` records: nothing
// lives at module scope in this file. frank.toJSON serialises each export on
// its own, so a module-scope table or regex would be unresolvable in the
// published bundle.

/**
 * The `auth.callback.error.*` key for a sign-in failure.
 *
 * `err` is whatever the page has in hand: an `Error` from the SDK, the
 * provider's own `error` / `error_description` value out of the callback
 * query (a bare string), or nothing at all. The answer is always a key that
 * exists in every shipped bundle, so the screen never shows a raw message and
 * never shows a bare key either.
 */
export const authCallbackErrorKey = (err) => {
  const text =
    typeof err === 'string'
      ? err
      : [err?.cause?.error, err?.code, err?.error, err?.message].filter(Boolean).join(' ')
  const is = (re) => re.test(text)

  // The user turned the authorization down. Not a fault — say so plainly.
  if (is(/access.?denied|user.?denied|consent.?required|cancell?ed/i)) {
    return 'auth.callback.error.accessDenied'
  }
  // GitHub's `unverified_user_email`, and the server's own `email_not_verified`
  // (the code `pages/auth/accept-invite.js` already classifies by name).
  if (is(/unverified.?user.?email|email.?not.?verified/i)) {
    return 'auth.callback.error.unverifiedEmail'
  }
  // A consumed or stale single-use credential: the magic link's token, or
  // GitHub's `bad_verification_code` for a code already exchanged.
  if (is(/expired|bad.?verification.?code|invalid.?grant|already.?used/i)) {
    return 'auth.callback.error.expired'
  }
  // The round trip never reached the server. `Load failed` is Safari's wording
  // for the same thing Chrome calls `Failed to fetch`.
  if (is(/failed to fetch|load failed|network|offline|timed? ?out|ERR_[A-Z_]+/i)) {
    return 'auth.callback.error.network'
  }
  // Configuration and outage: `redirect_uri_mismatch`,
  // `incorrect_client_credentials`, `application_suspended`, a 5xx, or the
  // magic page's own `SDK token manager unavailable`. None of them is
  // anything the person in front of the screen can act on, so they share one
  // message that says to come back.
  if (
    is(
      /redirect.?uri|client.?credentials|application.?suspended|unavailable|unsupported|not configured|50[0-4]|bad gateway|internal server/i
    )
  ) {
    return 'auth.callback.error.unavailable'
  }
  return 'auth.callback.error.generic'
}
